import { useState, useEffect } from 'react'
import { api } from '../services/api'
import { valorPorExtenso, formatarMoeda, dataHoje } from '../services/utils'
import { getCurrentUser } from '../services/auth'

// Converte "1.550,00" → 1550.00 (formato BR → float)
function parseBRL(str) {
  if (!str) return 0
  return parseFloat(String(str).replace(/\./g, '').replace(',', '.')) || 0
}

// Retorna extenso capitalizado: "mil e quinhentos reais" → "Mil e quinhentos reais"
function extensoCapitalizado(valor) {
  const txt = valorPorExtenso(valor)
  if (!txt) return ''
  return txt.charAt(0).toUpperCase() + txt.slice(1)
}

// ─── Registro de tipos de documento ──────────────────────────────────────────
// Para adicionar um novo tipo: insira um objeto neste array.
// disponivel: false → aparece como card "Em breve" (sem formulário).
const TIPOS = [
  {
    id: 'contrato_app',
    titulo: 'Contrato para App',
    descricao: 'Locação via aplicativo de transporte',
    minRole: 'basico',
    disponivel: true,
  },
  {
    id: 'confissao_divida',
    titulo: 'Confissão de Dívida',
    descricao: 'Termo de confissão e parcelamento de débito',
    minRole: 'intermediario',
    disponivel: true,
  },
  {
    id: 'declaracao_culpa',
    titulo: 'Declaração de Culpa',
    descricao: 'Acidente de trânsito',
    minRole: 'intermediario',
    disponivel: false,
  },
  {
    id: 'quitacao_sinistro',
    titulo: 'Quitação de Sinistro',
    descricao: 'Dívida decorrente de acidente de trânsito',
    minRole: 'intermediario',
    disponivel: false,
  },
  {
    id: 'bem_garantia',
    titulo: 'Bem em Garantia',
    descricao: 'Garantia de pagamento de dívida',
    minRole: 'intermediario',
    disponivel: false,
  },
  {
    id: 'notificacao_extrajudicial',
    titulo: 'Notificação Extrajudicial',
    descricao: 'Notificação formal de débito ou obrigação',
    minRole: 'intermediario',
    disponivel: true,
  },
  {
    id: 'acordo_serasa',
    titulo: 'Acordo Extrajudicial',
    descricao: 'Confissão de dívida e parcelamento — Serasa',
    minRole: 'intermediario',
    disponivel: true,
  },
  {
    id: 'nota_promissoria',
    titulo: 'Retirada de Nota Promissória',
    descricao: 'Declaração de retirada presencial da nota promissória assinada na locação',
    minRole: 'intermediario',
    disponivel: true,
  },
]

const ROLE_ORDER = { basico: 0, intermediario: 1, master: 2 }
const ESTADOS_CIVIS = ['solteiro', 'casado', 'divorciado', 'viúvo', 'separado', 'união estável']

const clienteVazio = {
  nome: '', cpf: '', rg: '', cnh: '', data_nascimento: '',
  estado_civil: 'solteiro', profissao: '',
  endereco: '', numero: '', complemento: '', bairro: '',
  cidade: 'Curitiba', estado: 'PR', cep: '',
  telefone: '', email: '',
  referencia1_nome: '', referencia1_telefone: '',
  referencia2_nome: '', referencia2_telefone: '',
  referencia3_nome: '', referencia3_telefone: '',
}

// ─── Componentes auxiliares ───────────────────────────────────────────────────
function Campo({ label, children, span2 }) {
  return (
    <div className="field" style={span2 ? { gridColumn: '1 / -1' } : {}}>
      {label && <label>{label}</label>}
      {children}
    </div>
  )
}

// ─── Bloco compartilhado: Veículo ─────────────────────────────────────────────
function BlocoVeiculo({ veiculos, veiculoId, setVeiculoId }) {
  const [modoVeiculo, setModoVeiculo] = useState('lista')
  const [buscaPlaca, setBuscaPlaca]   = useState('')
  const [erro, setErro] = useState('')

  const selecionado = veiculos.find(v => v.id === veiculoId)

  const buscarPlaca = () => {
    const placa = buscaPlaca.toUpperCase().trim()
    const found = veiculos.find(v =>
      v.placa.toUpperCase() === placa ||
      v.placa.replace('-', '').toUpperCase() === placa.replace('-', '')
    )
    if (found) { setVeiculoId(found.id); setErro('') }
    else setErro(`Placa ${placa} não encontrada.`)
  }

  return (
    <div className="card">
      <h2>Veículo</h2>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {['lista', 'placa'].map(m => (
          <button key={m}
            onClick={() => { setModoVeiculo(m); setVeiculoId(''); setBuscaPlaca(''); setErro('') }}
            style={{ fontSize: '12px', background: modoVeiculo === m ? 'var(--active-bg)' : undefined, borderColor: modoVeiculo === m ? '#C9A84C' : undefined }}>
            {m === 'lista' ? 'Selecionar da lista' : 'Buscar por placa'}
          </button>
        ))}
      </div>

      {modoVeiculo === 'lista' ? (
        <Campo label="Veículo *">
          <select value={veiculoId} onChange={e => setVeiculoId(e.target.value)}>
            <option value="">— escolha o veículo —</option>
            {veiculos.map(v => (
              <option key={v.id} value={v.id}>{v.placa} · {v.modelo} · {v.ano} · {v.cor}</option>
            ))}
          </select>
        </Campo>
      ) : (
        <Campo label="Placa *">
          <div style={{ display: 'flex', gap: '8px' }}>
            <input type="text" value={buscaPlaca}
              onChange={e => setBuscaPlaca(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscarPlaca()}
              placeholder="Ex: RME-5J31"
              style={{ textTransform: 'uppercase', flex: 1 }} />
            <button onClick={buscarPlaca} style={{ whiteSpace: 'nowrap', width: 'auto' }}>Buscar</button>
          </div>
        </Campo>
      )}

      {erro && <div className="badge-err" style={{ marginTop: '8px' }}>{erro}</div>}
      {selecionado && (
        <div className="badge-ok" style={{ marginTop: '10px' }}>
          {selecionado.placa} · {selecionado.marca} {selecionado.modelo} · {selecionado.ano} · {selecionado.cor}
        </div>
      )}
    </div>
  )
}

// ─── Bloco compartilhado: Cliente ─────────────────────────────────────────────
function BlocoCliente({ clientes, setClientes, clienteId, setClienteId, cliente, setCliente, modoCliente, setModoCliente, isBasico }) {
  const [buscaCliente, setBuscaCliente] = useState('')

  const selecionado = clientes.find(c => c.id === clienteId)
  const filtrados = clientes.filter(c =>
    buscaCliente.length >= 2 && (
      c.nome.toLowerCase().includes(buscaCliente.toLowerCase()) ||
      c.cpf.includes(buscaCliente)
    )
  )
  const setC = k => e => setCliente(p => ({ ...p, [k]: e.target.value }))

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, border: 'none', padding: 0 }}>Cliente</h2>
        {!isBasico && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {['buscar', 'novo'].map(m => (
              <button key={m}
                onClick={() => {
                  setModoCliente(m)
                  if (m === 'buscar') setCliente(clienteVazio)
                  else { setClienteId(''); setBuscaCliente('') }
                }}
                style={{ fontSize: '12px', background: modoCliente === m ? 'var(--active-bg)' : undefined, borderColor: modoCliente === m ? '#C9A84C' : undefined }}>
                {m === 'buscar' ? 'Buscar cadastrado' : 'Novo cliente'}
              </button>
            ))}
          </div>
        )}
      </div>

      {(modoCliente === 'buscar' || isBasico) ? (
        <div>
          <Campo label="Buscar por nome ou CPF">
            <input type="text" value={buscaCliente}
              onChange={e => { setBuscaCliente(e.target.value); setClienteId('') }}
              placeholder="Digite pelo menos 2 caracteres..." />
          </Campo>
          {buscaCliente.length >= 2 && (
            <div style={{ marginTop: '8px', border: '1px solid var(--card-border)', borderRadius: '14px', overflow: 'hidden' }}>
              {filtrados.length === 0 ? (
                <p style={{ padding: '12px', margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Nenhum cliente encontrado.{!isBasico ? ' Use "Novo cliente".' : ''}
                </p>
              ) : filtrados.map(c => (
                <div key={c.id}
                  onClick={() => { setClienteId(c.id); setBuscaCliente(c.nome) }}
                  style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px',
                    background: clienteId === c.id ? 'var(--active-bg)' : 'var(--card-bg)',
                    borderBottom: '1px solid var(--section-border)' }}>
                  <span style={{ fontWeight: clienteId === c.id ? 'bold' : 'normal' }}>{c.nome}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px', marginLeft: '10px' }}>{c.cpf}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px', marginLeft: '10px' }}>{c.telefone}</span>
                </div>
              ))}
            </div>
          )}
          {selecionado && (
            <div className="badge-ok" style={{ marginTop: '10px' }}>
              {selecionado.nome} · CPF {selecionado.cpf} · {selecionado.telefone}
            </div>
          )}
        </div>
      ) : (
        // Formulário de novo cliente
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Campo label="Nome completo *" span2><input type="text" value={cliente.nome} onChange={setC('nome')} /></Campo>
          <Campo label="CPF *"><input type="text" value={cliente.cpf} onChange={setC('cpf')} placeholder="000.000.000-00" /></Campo>
          <Campo label="RG *"><input type="text" value={cliente.rg} onChange={setC('rg')} /></Campo>
          <Campo label="CNH *"><input type="text" value={cliente.cnh} onChange={setC('cnh')} /></Campo>
          <Campo label="Data de nascimento"><input type="date" value={cliente.data_nascimento} onChange={setC('data_nascimento')} /></Campo>
          <Campo label="Estado civil *">
            <select value={cliente.estado_civil} onChange={setC('estado_civil')}>
              {ESTADOS_CIVIS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </Campo>
          <Campo label="Profissão *"><input type="text" value={cliente.profissao} onChange={setC('profissao')} /></Campo>
          <Campo label="Telefone *"><input type="text" value={cliente.telefone} onChange={setC('telefone')} placeholder="(41) 99999-9999" /></Campo>
          <Campo label="E-mail"><input type="email" value={cliente.email} onChange={setC('email')} /></Campo>

          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--section-border)', paddingTop: '14px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px' }}>Endereço</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Campo label="Rua / Avenida *" span2><input type="text" value={cliente.endereco} onChange={setC('endereco')} /></Campo>
              <Campo label="Número *"><input type="text" value={cliente.numero} onChange={setC('numero')} /></Campo>
              <Campo label="Complemento"><input type="text" value={cliente.complemento} onChange={setC('complemento')} /></Campo>
              <Campo label="Bairro *"><input type="text" value={cliente.bairro} onChange={setC('bairro')} /></Campo>
              <Campo label="CEP *"><input type="text" value={cliente.cep} onChange={setC('cep')} placeholder="00000-000" /></Campo>
              <Campo label="Cidade *"><input type="text" value={cliente.cidade} onChange={setC('cidade')} /></Campo>
              <Campo label="UF *"><input type="text" value={cliente.estado} onChange={setC('estado')} /></Campo>
            </div>
          </div>

          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--section-border)', paddingTop: '14px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px' }}>Contatos de Referência *</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[1, 2, 3].map(n => (
                <div key={n} style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: 'var(--text-secondary)', letterSpacing: '0.06em' }}>Referência {n}</div>
                  <Campo label="Nome *"><input type="text" value={cliente[`referencia${n}_nome`]} onChange={setC(`referencia${n}_nome`)} placeholder="Nome completo" /></Campo>
                  <Campo label="Telefone *"><input type="text" value={cliente[`referencia${n}_telefone`]} onChange={setC(`referencia${n}_telefone`)} placeholder="(00) 00000-0000" /></Campo>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Converte inteiro para extenso sem "reais/real" (ex: 12 → "doze")
function extensoNumero(n) {
  const txt = valorPorExtenso(parseInt(n, 10))
  if (!txt) return ''
  return txt.replace(/ reais?$/i, '').trim()
}

// ─── Campos específicos por tipo ──────────────────────────────────────────────
function CamposContrato({ dados, onChange }) {
  const set = k => e => onChange({ ...dados, [k]: e.target.value })
  return (
    <div className="card">
      <h2>Valores e Data</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        <Campo label="Data de início *">
          <input type="date" value={dados.data_inicio} onChange={set('data_inicio')} />
        </Campo>
        <Campo label="Valor semanal (R$) *">
          <input type="number" value={dados.valor_semanal} onChange={set('valor_semanal')} placeholder="350" min="0" step="0.01" />
          {dados.valor_semanal && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{valorPorExtenso(dados.valor_semanal)}</span>}
        </Campo>
        <Campo label="Caução (R$) *">
          <input type="number" value={dados.caucao} onChange={set('caucao')} placeholder="700" min="0" step="0.01" />
          {dados.caucao && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{valorPorExtenso(dados.caucao)}</span>}
        </Campo>
      </div>
    </div>
  )
}

function CamposNotificacao({ dados, onChange }) {
  const set = k => e => onChange({ ...dados, [k]: e.target.value })

  const extensoDivida = extensoCapitalizado(parseBRL(dados.valor_total))
  const extensoCaucao = extensoCapitalizado(parseBRL(dados.valor_caucao))

  return (
    <div className="card">
      <h2>Dados da Notificação</h2>

      {/* Modelo: Com Caução ou Sem Caução */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px' }}>
          Modelo
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { val: false, label: 'Sem Caução — veículo resgatado por inadimplência' },
            { val: true,  label: 'Com Caução — cliente devolveu com débito em aberto' },
          ].map(({ val, label }) => (
            <button key={String(val)}
              onClick={() => onChange({ ...dados, com_caucao: val })}
              style={{
                fontSize: '12px', flex: 1, textAlign: 'left', padding: '10px 14px',
                background: dados.com_caucao === val ? 'var(--active-bg)' : undefined,
                borderColor: dados.com_caucao === val ? '#C9A84C' : undefined,
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Campo label="Data da Notificação *">
          <input type="date" value={dados.data_notificacao} onChange={set('data_notificacao')} />
        </Campo>

        <Campo label="Data de Encerramento do Contrato *">
          <input type="date" value={dados.data_encerramento} onChange={set('data_encerramento')} />
        </Campo>

        <Campo label="Valor Total da Dívida (R$) *">
          <input type="text" value={dados.valor_total} onChange={set('valor_total')}
            placeholder="Ex: 1.775,96" />
          {extensoDivida && (
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {extensoDivida}
            </span>
          )}
        </Campo>

        {dados.com_caucao && (
          <Campo label="Valor da Caução (R$) *">
            <input type="text" value={dados.valor_caucao || ''} onChange={set('valor_caucao')}
              placeholder="Ex: 999,00" />
            {extensoCaucao && (
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {extensoCaucao}
              </span>
            )}
          </Campo>
        )}
      </div>
    </div>
  )
}

function CamposConfissao({ dados, onChange }) {
  const set = k => e => onChange({ ...dados, [k]: e.target.value })
  return (
    <div className="card">
      <h2>Dados do Termo</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Campo label="Valor da Dívida (R$) *">
          <input type="text" value={dados.valor_divida} onChange={set('valor_divida')} placeholder="Ex: 1.150,00" />
        </Campo>
        <Campo label="Data Limite de Pagamento *">
          <input type="date" value={dados.data_pagamento} onChange={set('data_pagamento')} />
        </Campo>
        <Campo label="Origem da Dívida *" span2>
          <input type="text" value={dados.origem_divida} onChange={set('origem_divida')} placeholder="Ex: avarias decorrentes de sinistro e débitos acessórios" />
        </Campo>
        <Campo label="Data de Assinatura *">
          <input type="date" value={dados.data_assinatura} onChange={set('data_assinatura')} />
        </Campo>
      </div>
    </div>
  )
}

function CamposAcordoSerasa({ dados, onChange }) {
  const set = k => e => onChange({ ...dados, [k]: e.target.value })

  const extensoTotal   = extensoCapitalizado(parseBRL(dados.valor_total_acordo))
  const extensoEntrada = extensoCapitalizado(parseBRL(dados.valor_entrada))
  const extensoParcela = extensoCapitalizado(parseBRL(dados.valor_parcela))

  return (
    <div className="card">
      <h2>Dados do Acordo</h2>

      {/* Origem da Dívida */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px' }}>Origem da Dívida</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Campo label="Origem / Descrição da dívida *" span2>
            <input type="text" value={dados.origem_divida || ''} onChange={set('origem_divida')}
              placeholder="Ex: aluguel de veículo, multas, avarias..." />
          </Campo>
          <Campo label="Data de origem *">
            <input type="date" value={dados.data_origem || ''} onChange={set('data_origem')} />
          </Campo>
          <Campo label="Valor original (R$) *">
            <input type="text" value={dados.valor_original || ''} onChange={set('valor_original')}
              placeholder="Ex: 1.500,00" />
          </Campo>
          <Campo label="Órgão de negativação *">
            <input type="text" value={dados.orgao_negativacao || ''} onChange={set('orgao_negativacao')}
              placeholder="Ex: Serasa, SPC..." />
          </Campo>
          <Campo label="Data de negativação *">
            <input type="date" value={dados.data_negativacao || ''} onChange={set('data_negativacao')} />
          </Campo>
        </div>
      </div>

      {/* Condições de Pagamento */}
      <div style={{ borderTop: '1px solid var(--section-border)', paddingTop: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px' }}>Condições de Pagamento</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Campo label="Valor total do acordo (R$) *">
            <input type="text" value={dados.valor_total_acordo || ''} onChange={set('valor_total_acordo')}
              placeholder="Ex: 1.200,00" />
            {extensoTotal && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{extensoTotal}</span>}
          </Campo>
          <Campo label="Valor de entrada (R$) *">
            <input type="text" value={dados.valor_entrada || ''} onChange={set('valor_entrada')}
              placeholder="Ex: 200,00" />
            {extensoEntrada && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{extensoEntrada}</span>}
          </Campo>
          <Campo label="Data da entrada *">
            <input type="date" value={dados.data_entrada || ''} onChange={set('data_entrada')} />
          </Campo>
          <Campo label="Quantidade de parcelas *">
            <input type="number" value={dados.qtd_parcelas || ''} onChange={set('qtd_parcelas')}
              placeholder="Ex: 6" min="1" step="1" />
            {dados.qtd_parcelas && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{extensoNumero(dados.qtd_parcelas)} parcelas</span>}
          </Campo>
          <Campo label="Valor da parcela (R$) *">
            <input type="text" value={dados.valor_parcela || ''} onChange={set('valor_parcela')}
              placeholder="Ex: 166,67" />
            {extensoParcela && <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{extensoParcela}</span>}
          </Campo>
          <Campo label="Data da 1ª parcela *">
            <input type="date" value={dados.data_1a_parcela || ''} onChange={set('data_1a_parcela')} />
          </Campo>
          <Campo label="Dia de vencimento (mensal) *">
            <input type="number" value={dados.dia_vencimento || ''} onChange={set('dia_vencimento')}
              placeholder="Ex: 5" min="1" max="28" />
          </Campo>
        </div>
      </div>

      {/* Condições Gerais */}
      <div style={{ borderTop: '1px solid var(--section-border)', paddingTop: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '12px' }}>Condições Gerais</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Campo label="Dias de tolerância">
            <input type="number" value={dados.dias_tolerancia ?? '5'} onChange={set('dias_tolerancia')}
              min="0" />
          </Campo>
          <Campo label="Índice de correção">
            <input type="text" value={dados.indice_correcao || 'IPCA'} onChange={set('indice_correcao')} />
          </Campo>
          <Campo label="Cidade do foro">
            <input type="text" value={dados.cidade_foro || 'Curitiba'} onChange={set('cidade_foro')} />
          </Campo>
          <Campo label="UF do foro">
            <input type="text" value={dados.estado_foro || 'PR'} onChange={set('estado_foro')} />
          </Campo>
        </div>
      </div>

      {/* Data de assinatura */}
      <div style={{ borderTop: '1px solid var(--section-border)', paddingTop: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Campo label="Data de assinatura *">
            <input type="date" value={dados.data_assinatura || ''} onChange={set('data_assinatura')} />
          </Campo>
        </div>
      </div>
    </div>
  )
}

function CamposNotaPromissoria({ dados, onChange }) {
  const set = k => e => onChange({ ...dados, [k]: e.target.value })
  return (
    <div className="card">
      <h2>Data de Retirada</h2>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 1rem' }}>
        Os dados do cliente e do veículo são preenchidos automaticamente a partir do cadastro.
        Informe apenas a data em que a nota promissória foi retirada.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Campo label="Data de retirada *">
          <input type="date" value={dados.data_retirada || ''} onChange={set('data_retirada')} />
        </Campo>
      </div>
    </div>
  )
}

// ─── Seletor de tipo de documento ─────────────────────────────────────────────
function SeletorTipo({ tipos, onSelecionar }) {
  return (
    <div>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 1.5rem' }}>
        Escolha o modelo de documento que deseja gerar.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
        {tipos.map(tipo => (
          <button
            key={tipo.id}
            onClick={() => tipo.disponivel && onSelecionar(tipo.id)}
            disabled={!tipo.disponivel}
            style={{
              background: tipo.disponivel ? 'var(--card-bg)' : 'var(--bg)',
              border: `1.5px solid ${tipo.disponivel ? 'var(--card-border)' : 'var(--border)'}`,
              borderRadius: '18px',
              padding: '1.25rem 1.5rem',
              textAlign: 'left',
              cursor: tipo.disponivel ? 'pointer' : 'default',
              opacity: tipo.disponivel ? 1 : 0.55,
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              height: '100%',
            }}
          >
            <span style={{
              fontSize: '11px', letterSpacing: '0.10em', textTransform: 'uppercase',
              color: tipo.disponivel ? '#C9A84C' : 'var(--text-secondary)',
              fontWeight: 'normal',
            }}>
              {tipo.disponivel ? 'Disponível' : 'Em breve'}
            </span>
            <span style={{ fontSize: '15px', color: 'var(--text-primary)', fontWeight: 'bold', lineHeight: 1.3 }}>
              {tipo.titulo}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
              {tipo.descricao}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function GerarDocumento() {
  const user     = getCurrentUser()
  const isBasico = user?.role === 'basico'
  const roleOrder = ROLE_ORDER[user?.role] ?? 0

  const tiposVisiveis = TIPOS.filter(t => roleOrder >= (ROLE_ORDER[t.minRole] ?? 0))

  const [tipo, setTipo]           = useState(null)
  const [veiculos, setVeiculos]   = useState([])
  const [clientes, setClientes]   = useState([])
  const [veiculoId, setVeiculoId] = useState('')
  const [clienteId, setClienteId] = useState('')
  const [modoCliente, setModoCliente] = useState('buscar')
  const [cliente, setCliente]     = useState(clienteVazio)
  const [camposDoc, setCamposDoc] = useState({})
  const [loading, setLoading]     = useState(false)
  const [erro, setErro]           = useState('')
  const [sucesso, setSucesso]     = useState('')

  useEffect(() => {
    api.veiculos.listar().then(setVeiculos).catch(() => {})
    api.clientes.listar().then(setClientes).catch(() => {})
  }, [])

  // Inicializa campos padrão conforme o tipo selecionado
  const selecionarTipo = (id) => {
    setTipo(id)
    setErro(''); setSucesso('')
    setVeiculoId(''); setClienteId('')
    setModoCliente('buscar'); setCliente(clienteVazio)
    if (id === 'contrato_app')    setCamposDoc({ data_inicio: dataHoje(), valor_semanal: '', caucao: '' })
    if (id === 'confissao_divida') setCamposDoc({ valor_divida: '', origem_divida: '', data_pagamento: '', data_assinatura: dataHoje() })
    if (id === 'notificacao_extrajudicial') setCamposDoc({
      data_notificacao: dataHoje(), data_encerramento: '',
      valor_total: '', valor_total_extenso: '',
      com_caucao: false, valor_caucao: '', valor_caucao_extenso: '',
    })
    if (id === 'nota_promissoria') setCamposDoc({ data_retirada: dataHoje() })
    if (id === 'acordo_serasa') setCamposDoc({
      origem_divida: '', data_origem: '', valor_original: '',
      orgao_negativacao: 'Serasa', data_negativacao: '',
      valor_total_acordo: '', valor_entrada: '', data_entrada: '',
      qtd_parcelas: '', valor_parcela: '', data_1a_parcela: '',
      dia_vencimento: '', dias_tolerancia: '5', indice_correcao: 'IPCA',
      cidade_foro: 'Curitiba', estado_foro: 'PR',
      data_assinatura: dataHoje(),
    })
  }

  const voltarAoSeletor = () => {
    setTipo(null); setErro(''); setSucesso('')
  }

  const validarCliente = () => {
    if (modoCliente === 'buscar' && !clienteId) return 'Selecione um cliente cadastrado.'
    if (modoCliente === 'novo') {
      if (!cliente.nome || !cliente.cpf || !cliente.rg || !cliente.cnh ||
          !cliente.profissao || !cliente.endereco || !cliente.numero ||
          !cliente.bairro || !cliente.cep || !cliente.telefone)
        return 'Preencha todos os campos obrigatórios do cliente.'
      if (!cliente.referencia1_nome || !cliente.referencia1_telefone ||
          !cliente.referencia2_nome || !cliente.referencia2_telefone ||
          !cliente.referencia3_nome || !cliente.referencia3_telefone)
        return 'Informe os 3 contatos de referência.'
    }
    return null
  }

  const handleGerar = async () => {
    setErro(''); setSucesso('')
    if (!tipoAtual?.semVeiculo && !veiculoId) return setErro('Selecione um veículo.')
    const erroCliente = validarCliente()
    if (erroCliente) return setErro(erroCliente)

    setLoading(true)
    try {
      // Cadastra novo cliente se necessário
      let cidFinal = clienteId
      if (modoCliente === 'novo') {
        const novo = await api.clientes.cadastrar(cliente)
        cidFinal = novo.id
        setClientes(prev => [...prev, novo])
      }

      const veiculo = veiculos.find(v => v.id === veiculoId)
      let blob, nomeArq

      if (tipo === 'nota_promissoria') {
        if (!camposDoc.data_retirada) return setErro('Informe a data de retirada.')
        blob = await api.notaPromissoria.gerar({
          cliente_id: cidFinal,
          veiculo_id: veiculoId,
          campos: { data_retirada: camposDoc.data_retirada },
        })
        nomeArq = `nota_promissoria_${Date.now()}.docx`
      }

      if (tipo === 'acordo_serasa') {
        const c = camposDoc
        if (!c.origem_divida)    return setErro('Informe a origem da dívida.')
        if (!c.data_origem)      return setErro('Informe a data de origem da dívida.')
        if (!c.valor_original)   return setErro('Informe o valor original da dívida.')
        if (!c.orgao_negativacao) return setErro('Informe o órgão de negativação.')
        if (!c.data_negativacao) return setErro('Informe a data de negativação.')
        if (!c.valor_total_acordo) return setErro('Informe o valor total do acordo.')
        if (!c.valor_entrada)    return setErro('Informe o valor de entrada.')
        if (!c.data_entrada)     return setErro('Informe a data da entrada.')
        if (!c.qtd_parcelas)     return setErro('Informe a quantidade de parcelas.')
        if (!c.valor_parcela)    return setErro('Informe o valor da parcela.')
        if (!c.data_1a_parcela)  return setErro('Informe a data da 1ª parcela.')
        if (!c.dia_vencimento)   return setErro('Informe o dia de vencimento.')
        if (!c.data_assinatura)  return setErro('Informe a data de assinatura.')

        const camposEnvio = {
          ...c,
          qtd_parcelas_extenso: extensoNumero(c.qtd_parcelas),
        }
        blob = await api.acordoSerasa.gerar({ cliente_id: cidFinal, veiculo_id: veiculoId, campos: camposEnvio })
        nomeArq = `acordo_serasa_${Date.now()}.docx`
      }

      if (tipo === 'contrato_app') {
        if (!camposDoc.valor_semanal) return setErro('Informe o valor semanal.')
        if (!camposDoc.caucao) return setErro('Informe o valor da caução.')
        blob = await api.contratos.gerar({
          veiculo_id: veiculoId,
          cliente_id: cidFinal,
          data_inicio: camposDoc.data_inicio,
          valor_semanal: formatarMoeda(camposDoc.valor_semanal),
          valor_semanal_extenso: valorPorExtenso(camposDoc.valor_semanal),
          caucao_valor: formatarMoeda(camposDoc.caucao),
          caucao_extenso: valorPorExtenso(camposDoc.caucao),
          data_assinatura: dataHoje(),
        })
        nomeArq = `contrato_${veiculo?.placa || 'doc'}_${Date.now()}.docx`
      }

      if (tipo === 'notificacao_extrajudicial') {
        if (!camposDoc.valor_total)       return setErro('Informe o valor total da dívida.')
        if (!camposDoc.data_encerramento) return setErro('Informe a data de encerramento do contrato.')
        if (camposDoc.com_caucao && !camposDoc.valor_caucao) return setErro('Informe o valor da caução.')
        const camposEnvio = {
          ...camposDoc,
          valor_total_extenso:  extensoCapitalizado(parseBRL(camposDoc.valor_total)),
          valor_caucao_extenso: camposDoc.com_caucao
            ? extensoCapitalizado(parseBRL(camposDoc.valor_caucao))
            : '',
        }
        blob = await api.notificacoes.gerar({
          cliente_id: cidFinal,
          veiculo_id: veiculoId,
          campos: camposEnvio,
        })
        const modelo = camposDoc.com_caucao ? 'com_caucao' : 'sem_caucao'
        nomeArq = `notificacao_${modelo}_${veiculo?.placa || 'doc'}_${Date.now()}.docx`
      }

      if (tipo === 'confissao_divida') {
        if (!camposDoc.valor_divida) return setErro('Informe o valor da dívida.')
        if (!camposDoc.origem_divida) return setErro('Informe a origem da dívida.')
        if (!camposDoc.data_pagamento) return setErro('Informe a data limite de pagamento.')
        blob = await api.termos.gerar({
          cliente_id: cidFinal,
          veiculo_id: veiculoId,
          campos: {
            valor_divida:    camposDoc.valor_divida,
            origem_divida:   camposDoc.origem_divida,
            data_pagamento:  camposDoc.data_pagamento,
            data_assinatura: camposDoc.data_assinatura,
          },
        })
        nomeArq = `confissao_divida_${veiculo?.placa || 'doc'}_${Date.now()}.docx`
      }

      const url = URL.createObjectURL(blob)
      const a   = document.createElement('a')
      a.href = url; a.download = nomeArq; a.click()
      URL.revokeObjectURL(url)

      setSucesso(`Documento gerado: ${nomeArq}`)
      setVeiculoId(''); setClienteId('')
      setCliente(clienteVazio); setModoCliente('buscar')
    } catch (e) {
      setErro(e.message)
    } finally {
      setLoading(false)
    }
  }

  const tipoAtual = TIPOS.find(t => t.id === tipo)

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        {tipo && (
          <button
            onClick={voltarAoSeletor}
            style={{ fontSize: '12px', padding: '6px 14px', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
          >
            ← Trocar
          </button>
        )}
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 'normal', letterSpacing: '0.06em', margin: 0, color: 'var(--text-primary)' }}>
            {tipo ? tipoAtual.titulo.toUpperCase() : 'GERAR DOCUMENTO'}
          </h1>
          {tipo && (
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {tipoAtual.descricao}
            </p>
          )}
        </div>
      </div>

      {/* Seletor de tipo */}
      {!tipo && <SeletorTipo tipos={tiposVisiveis} onSelecionar={selecionarTipo} />}

      {/* Formulário dinâmico */}
      {tipo && (
        <>
          {!tipoAtual?.semVeiculo && (
            <BlocoVeiculo
              veiculos={veiculos}
              veiculoId={veiculoId}
              setVeiculoId={setVeiculoId}
            />
          )}

          <BlocoCliente
            clientes={clientes}
            setClientes={setClientes}
            clienteId={clienteId}
            setClienteId={setClienteId}
            cliente={cliente}
            setCliente={setCliente}
            modoCliente={modoCliente}
            setModoCliente={setModoCliente}
            isBasico={isBasico}
          />

          {tipo === 'contrato_app' && (
            <CamposContrato dados={camposDoc} onChange={setCamposDoc} />
          )}
          {tipo === 'confissao_divida' && (
            <CamposConfissao dados={camposDoc} onChange={setCamposDoc} />
          )}
          {tipo === 'notificacao_extrajudicial' && (
            <CamposNotificacao dados={camposDoc} onChange={setCamposDoc} />
          )}
          {tipo === 'nota_promissoria' && (
            <CamposNotaPromissoria dados={camposDoc} onChange={setCamposDoc} />
          )}
          {tipo === 'acordo_serasa' && (
            <CamposAcordoSerasa dados={camposDoc} onChange={setCamposDoc} />
          )}

          {erro    && <div className="badge-err" style={{ marginBottom: '1rem' }}>{erro}</div>}
          {sucesso && <div className="badge-ok"  style={{ marginBottom: '1rem' }}>{sucesso}</div>}

          <button
            className="btn-gold"
            onClick={handleGerar}
            disabled={loading}
            style={{ fontSize: '14px', padding: '14px' }}
          >
            {loading ? 'GERANDO DOCUMENTO...' : `GERAR ${tipoAtual.titulo.toUpperCase()} .DOCX`}
          </button>
        </>
      )}
    </div>
  )
}
